import { assignInlineVars } from '@vanilla-extract/dynamic';

import {
  type TruncationMode,
  _centerEllipsizeText,
  _getTextHeight,
  _getTextWidth,
} from '@dynatrace/strato-components/typography';

import { axisFontProps } from '../../../../core/components/axes/constants.js';
import { TRUNCATION_MODE } from '../../../../core/constants.js';
import { useComputedFontStyle } from '../../../../core/hooks/useComputedFontStyle.js';
import {
  directionValue,
  tickStyle,
  widthContainer,
} from '../AxisTickWithTruncation.sty.js';

export function useTickTruncation(
  formattedValue: string | undefined,
  maxSpaceForTruncation: number,
  truncationMode: TruncationMode,
) {
  const fontStyle = useComputedFontStyle(axisFontProps);

  const truncatedValue = _centerEllipsizeText(
    formattedValue?.toString() || '',
    fontStyle,
    maxSpaceForTruncation,
  );

  const truncatedHeight = _getTextHeight(truncatedValue ?? '', fontStyle);
  const truncatedWidth = _getTextWidth(truncatedValue, fontStyle) ?? 0;

  /**
   * A const to easily check whether the truncationMode props is set to Middle
   * or not.
   **/
  const IS_TRUNCATED_IN_MIDDLE = truncationMode === TRUNCATION_MODE.Middle;

  /**
   * The value that should be displayed in the component. If the truncation
   * is set to the middle the truncation will bet performed programmatically for
   * for other cases will be handled by CSS only.
   **/
  const displayValue = IS_TRUNCATED_IN_MIDDLE ? truncatedValue : formattedValue;

  /**
   * A value to switch the text direction in order to place the truncation
   * symbol in the beginning or end of the display value.
   **/
  const direction = truncationMode === TRUNCATION_MODE.Start ? 'rtl' : 'ltr';

  /**
   * The width to be used by containers in order to make the truncation works.
   * The value should be rounded to avoid a premature truncation in Safari that
   * consider a fractional pixel to truncate.
   **/
  const WIDTH = Math.ceil(truncatedWidth);

  /**
   * The class name that will be applied to the tick container mainly if the
   * truncation is being set to the right or left side.
   **/
  const tickClassName = !IS_TRUNCATED_IN_MIDDLE ? tickStyle : undefined;

  /**
   * A variable that contains the inline styles to be used in the tick
   * container, to override the properties in the CSS class name,  once the styles
   * have been moved to the vanilla-extract implementation.
   **/
  const tickInlineStyle = !IS_TRUNCATED_IN_MIDDLE
    ? assignInlineVars({
        [directionValue]: direction,
        [widthContainer]: `${WIDTH}px`,
      })
    : { textWrap: 'nowrap' };

  return {
    displayValue,
    tickClassName,
    tickInlineStyle,
    truncatedWidth,
    truncatedHeight,
    truncatedValue,
  };
}
